import {
	Button,
	HStack,
	IconAdd,
	IconButton,
	IconDelete,
	IconEdit,
	Tab,
	TabPanel,
	TabPanels,
	Tabs,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import { FunctionComponent, useEffect, useState } from 'react';
import PermissionsModal from '../PermissionsForm/PermissionsModal';
import { ApplicationPermissions } from '../SubscriptionFormContext';
import { SubscriberType } from 'domain/subscription/interfaces/SubscriberType';
import PermissionsFormContextProvider from '../PermissionsForm/PermissionsFormContext';
import { ProductIcon } from 'components/common/Product/ProductIcon/ProductIcon';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { BundlePermissions } from 'domain/subscription/interfaces/SubscriptionBundle';

const EditApplicationTabs: FunctionComponent<{
	applicationsPermissions: ApplicationPermissions[];
	onPermissionsAdded: (
		applicationPermissions: ApplicationPermissions,
	) => void;
	onPermissionsEdited: (
		applicationPermissions: ApplicationPermissions,
	) => void;
	onPermissionsRemoved: (applicationId: string) => void;
	subscriberType: SubscriberType;
	bundle?: SubscriptionBundle;
	companyId?: number;
}> = ({
	applicationsPermissions,
	onPermissionsAdded,
	onPermissionsEdited,
	onPermissionsRemoved,
	subscriberType,
	bundle = undefined,
	companyId,
}) => {
	const { getMinimalAreaRepresentation } = useGlobalContext();
	const [permissionModalOpen, setPermissionModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('');
	const [editingPermissions, setEditingPermissions] = useState<
		ApplicationPermissions | undefined
	>(undefined);

	useEffect(() => {
		if (applicationsPermissions.length > 0 && activeTab === '') {
			setActiveTab(applicationsPermissions[0].application.id);
		}
	}, [applicationsPermissions]);

	useEffect(() => {
		if (editingPermissions) {
			setPermissionModalOpen(true);
		}
	}, [editingPermissions]);

	return (
		<VStack justify="center" fullWidth gap="xl">
			{applicationsPermissions.length === 0 ? (
				<Button
					text="Add application permissions"
					leftIcon={<IconAdd />}
					onClick={() => setPermissionModalOpen(true)}
				/>
			) : (
				<HStack justify="flex-start" align="center" fullWidth>
					<Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
						{applicationsPermissions.map((appPermissions) => (
							<Tab
								key={appPermissions.application.id}
								value={appPermissions.application.id}
								label={appPermissions.application.name}
								Icon={
									<ProductIcon
										productId={
											appPermissions.application.id
										}
										fontSize="sm"
										size="lg"
									/>
								}
							/>
						))}
					</Tabs>
					<IconButton
						icon={<IconAdd />}
						variant="tertiary"
						onClick={() => setPermissionModalOpen(true)}
					/>
				</HStack>
			)}
			<TabPanels currentTab={activeTab}>
				{applicationsPermissions.map((appPermissions) => (
					<TabPanel
						key={appPermissions.application.id}
						name={appPermissions.application.id}
					>
						<VStack fullWidth>
							<HStack justify="space-between" fullWidth>
								<LabelAndValue
									label="Plan"
									value={appPermissions.plan?.displayName}
								/>
								{(() => {
									const inheritedPermissions:
										| BundlePermissions
										| undefined =
										bundle?.productId ===
										appPermissions.application.id
											? bundle?.permissions
											: undefined;
									const hasInheritedAreas =
										(inheritedPermissions?.countries
											?.length ?? 0) > 0;
									return hasInheritedAreas ? (
										<LabelAndValue
											label="Areas (from bundle)"
											value={getMinimalAreaRepresentation(
												appPermissions.application.id,
												inheritedPermissions?.countries ??
													[],
											)?.map((country) => (
												<VStack key={country?.code}>
													<span>{country?.name}</span>
												</VStack>
											))}
										/>
									) : (
										<LabelAndValue
											label="Areas"
											value={getMinimalAreaRepresentation(
												appPermissions.application.id,
												appPermissions.countries.map(
													(c) => c.code,
												),
											)?.map((country) => (
												<VStack key={country?.code}>
													<span>{country?.name}</span>
												</VStack>
											))}
										/>
									);
								})()}
								<LabelAndValue
									label="Addons"
									value={appPermissions.addons.map(
										(addon) => (
											<VStack key={addon?.code}>
												<span>{addon?.name}</span>
											</VStack>
										),
									)}
								/>
								<HStack>
									<IconButton
										icon={<IconEdit />}
										onClick={() => {
											setEditingPermissions(
												appPermissions,
											);
										}}
										variant={'secondary'}
									/>
									<IconButton
										icon={<IconDelete />}
										onClick={() => {
											onPermissionsRemoved(
												appPermissions.application.id,
											);
											setActiveTab(
												applicationsPermissions[0]
													?.application.id || '',
											);
										}}
										variant={'negative'}
									/>
								</HStack>
							</HStack>
						</VStack>
					</TabPanel>
				))}
			</TabPanels>
			<PermissionsFormContextProvider
				subscriberType={subscriberType}
				bundleId={bundle?.id}
				companyId={companyId}
				onPermissionsAdded={(appPermissions) => {
					onPermissionsAdded(appPermissions);
					setPermissionModalOpen(false);
					setActiveTab(appPermissions.application.id);
				}}
				onPermissionsEdited={(appPermissions) => {
					onPermissionsEdited(appPermissions);
					setPermissionModalOpen(false);
					setActiveTab(appPermissions.application.id);
				}}
				editingPermissions={editingPermissions}
			>
				<PermissionsModal
					isOpen={permissionModalOpen}
					onClose={() => {
						setEditingPermissions(undefined);
						setPermissionModalOpen(false);
					}}
				/>
			</PermissionsFormContextProvider>
		</VStack>
	);
};

export default EditApplicationTabs;
